import * as React from "react";
import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { EAIClient } from "Services/EAIClient";
import { useToast } from "@/hooks/useToast";
import { CreateSavedViewPayload, revalidateSavedViews } from "@/hooks/saved-views";

interface SaveViewModalProps {
    show: boolean;
    dashboardId: number;
    /**
     * Captures the current Tableau view as a snapshot/custom-view URL — the same
     * URL mechanism used for action plan dashboard snapshots. May be slow (it
     * saves a Tableau custom view), so it is awaited while the spinner shows.
     */
    getUrl: () => Promise<string | undefined>;
    onClose: () => void;
    /** Called after a view is saved successfully (e.g. to refresh the in-dashboard Views panel). */
    onSaved?: () => void;
}

/**
 * Modal for saving the current Tableau dashboard state as a named, reusable view.
 * Name is required; description is optional. On confirm it captures the current
 * dashboard URL and POSTs a new saved view.
 */
export function SaveViewModal({ show, dashboardId, getUrl, onClose, onSaved }: SaveViewModalProps) {
    const { toast } = useToast();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset the form each time the modal is (re)opened.
    useEffect(() => {
        if (show) {
            setName("");
            setDescription("");
            setError(null);
            setIsSaving(false);
        }
    }, [show]);

    const trimmedName = name.trim();
    const canSave = trimmedName.length > 0 && !isSaving;

    const handleSave = async () => {
        if (!canSave) return;

        setIsSaving(true);
        setError(null);
        try {
            const url = await getUrl();
            if (!url) {
                setError("Could not capture the current dashboard view. Please try again.");
                return;
            }

            const payload: CreateSavedViewPayload = {
                dashboard_id: dashboardId,
                name: trimmedName,
                url,
            };
            const trimmedDescription = description.trim();
            if (trimmedDescription) {
                payload.description = trimmedDescription;
            }

            await new EAIClient().createSavedView(payload);
            // Refresh any mounted Saved Views panel so the new view shows immediately.
            void revalidateSavedViews();
            toast({ type: "success", title: "View saved", message: `"${trimmedName}" has been saved.` });
            onSaved?.();
            onClose();
        } catch (e: any) {
            setError(
                e?.response?.data?.result?.message ||
                    e?.message ||
                    "Something went wrong while saving the view. Please try again."
            );
        } finally {
            setIsSaving(false);
        }
    };

    // Enter key inside the form submits without closing on failure.
    const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        handleSave();
    };

    return (
        <Modal show={show} onHide={onClose} centered backdrop={isSaving ? "static" : true}>
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold" style={{ lineHeight: "36px", fontSize: "24px" }}>
                    Save View
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form id="save-view-form" onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            data-test-id="save-view-name-input"
                            placeholder="Name this view"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isSaving}
                            autoFocus
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-1">
                        <Form.Label>
                            Description <span className="text-muted">(optional)</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            data-test-id="save-view-description-input"
                            placeholder="Add a description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={isSaving}
                        />
                    </Form.Group>
                    {error && (
                        <p className="text-danger mt-2 mb-0" data-test-id="save-view-error">
                            {error}
                        </p>
                    )}
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-secondary"
                    style={{ width: "47%" }}
                    onClick={onClose}
                    disabled={isSaving}
                    id="cancelSaveViewButton"
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    style={{ marginLeft: 15, width: "46%" }}
                    onClick={handleSave}
                    disabled={!canSave}
                    data-test-id="save-view-confirm-btn"
                    id="confirmSaveViewButton"
                >
                    {isSaving ? <Spinner animation="border" size="sm" /> : "Save View"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
